/**
 * StructureCard — how the article measured against the platform's article
 * shape (heading counts, FAQ, CTA, callouts …) at generation time.
 *
 * The gate is non-blocking: articles ship even when they fall short, so this
 * card is the only place a reviewer can see that a published article is
 * structurally thin. It reads the verdict already carried on the content
 * detail response (generation_metadata.quality_gate_result
 * .agent_sdk_article_shape) — no extra fetch.
 *
 * Three states, deliberately quiet:
 *   pass    → "Meets structure standard"
 *   fail    → "Below structure standard" + one row per failed requirement
 *   absent  → "Structure wasn't checked for this article."
 */

import React from 'react';
import { Card, Tag, Tooltip } from 'antd';
import { CheckCircle, LayoutList } from 'lucide-react';
import { parseShapeErrors, type ArticleShapeReport } from '../articleShape';

interface StructureCardProps {
  /** Gate verdict, or null when this article never ran the shape gate. */
  shape: ArticleShapeReport | null | undefined;
}

const StructureCard: React.FC<StructureCardProps> = ({ shape }) => {
  const shortfalls = parseShapeErrors(shape?.errors);
  // A recorded `passed: true` with no errors is the only pass; anything else
  // with errors is a fail (the gate never ships a passing verdict with errors).
  const passed = Boolean(shape?.passed) && shortfalls.length === 0;

  return (
    <Card
      size="small"
      className="border border-border shadow-sm"
      bodyStyle={{ padding: '12px 16px' }}
      data-testid="structure-card"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <LayoutList size={16} className="text-muted-foreground" />
          <span className="text-sm font-semibold text-foreground">Structure</span>
        </div>
        {!shape ? (
          <Tag color="default" className="m-0">
            Not checked
          </Tag>
        ) : passed ? (
          <Tag color="success" className="m-0">
            Meets standard
          </Tag>
        ) : (
          <Tag color="warning" className="m-0">
            {shortfalls.length} shortfall{shortfalls.length !== 1 ? 's' : ''}
          </Tag>
        )}
      </div>

      {!shape ? (
        <p className="mt-2 text-xs text-muted-foreground">
          Structure wasn't checked for this article.
        </p>
      ) : passed ? (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
          <CheckCircle size={14} className="text-teal-dark shrink-0" />
          <span>Meets structure standard</span>
        </div>
      ) : (
        <>
          <p className="mt-2 text-xs text-muted-foreground">
            Below structure standard — measured against the platform minimums.
          </p>
          <ul className="mt-2 space-y-1">
            {shortfalls.map((s, i) => (
              <li
                key={`${s.key}-${i}`}
                className="flex items-baseline justify-between gap-3 text-xs"
              >
                <Tooltip title={s.raw}>
                  <span className="min-w-0 truncate text-foreground">{s.label}</span>
                </Tooltip>
                {s.measured !== null && s.required !== null && (
                  <span className="shrink-0 tabular-nums font-medium text-coral-dark">
                    {s.measured} / {s.required}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
};

export default StructureCard;
